import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { frontDeskApi } from '../../api/frontDesk';
import { luxisttConfirm } from '../../utils/luxisttSwal';
import {
  formatWholeNairaFromNumber,
  formatWholeNairaInputChange,
  parseWholeNairaToNumber,
} from '../../utils/moneyInputFormat';
import { FdPerPageField, FdPaginationNav, normalizeFdPerPage } from '../../components/FdDataTableControls';

function formatNgn(n) {
  if (n == null || n === '') return '—';
  try {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      maximumFractionDigits: 0,
    }).format(Number(n));
  } catch {
    return `₦${n}`;
  }
}

function toDateInput(v) {
  if (v == null || v === '') return '';
  const s = typeof v === 'string' ? v : String(v);
  return s.slice(0, 10);
}

function EmptyTableRow({ colSpan, message }) {
  return (
    <tr>
      <td colSpan={colSpan} className="fd-table-empty">
        <span>{message}</span>
      </td>
    </tr>
  );
}

const KIND_META = {
  discounts: {
    title: 'Discounts',
    lead: 'Percentage discounts for this property only. Property-wide (empty apartment) applies to every unit here, same as surcharges. Requires catalog discount permissions on your assignment.',
  },
  surcharges: {
    title: 'Surcharges',
    lead: 'Fees and charges (fixed or percent of room). Same rules as admin catalog; scoped to the selected property.',
  },
  'blocked-dates': {
    title: 'Block dates',
    lead: 'Close units for maintenance or holds. Matches blocked-date rules from the admin panel.',
  },
};

/**
 * @param {{
 *   kind: 'discounts'|'surcharges'|'blocked-dates',
 *   propertyId: string,
 *   apartmentOptions: { id: number, name: string }[],
 *   canView: boolean,
 *   canManage: boolean,
 *   setError: (s: string) => void,
 *   setSuccessMessage: (s: string) => void,
 *   refreshTick?: number,
 * }} props
 */
export function FrontDeskCatalogPanel({
  kind,
  propertyId,
  apartmentOptions,
  canView,
  canManage,
  setError,
  setSuccessMessage,
  refreshTick = 0,
}) {
  const meta = KIND_META[kind];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [surchargeTypes, setSurchargeTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [priceRows, setPriceRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const [discountForm, setDiscountForm] = useState({
    name: '',
    percent: '',
    min_nights: '',
    max_nights: '',
    valid_from: '',
    valid_to: '',
    is_active: true,
    sort_order: 0,
    apartment_id: '',
    apartment_price_id: '',
  });

  const [surchargeForm, setSurchargeForm] = useState({
    surcharge_type_id: '',
    name: '',
    billing_kind: 'fixed',
    amount: '',
    percent: '',
    min_nights: '',
    max_nights: '',
    valid_from: '',
    valid_to: '',
    is_active: true,
    is_mandatory: false,
    sort_order: 0,
    apartment_id: '',
    apartment_price_id: '',
  });

  const [blockedForm, setBlockedForm] = useState({
    apartment_id: '',
    date_from: '',
    date_to: '',
    reason: '',
  });

  const loadList = useCallback(async () => {
    if (!propertyId || !canView) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      let res;
      if (kind === 'discounts') res = await frontDeskApi.discounts(propertyId);
      else if (kind === 'surcharges') res = await frontDeskApi.surcharges(propertyId);
      else res = await frontDeskApi.blockedDates(propertyId);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setRows([]);
      setError(e.message || 'Could not load catalog.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, canView, kind, setError]);

  useEffect(() => {
    loadList();
  }, [loadList, refreshTick]);

  useEffect(() => {
    if (kind !== 'surcharges') return;
    (async () => {
      try {
        const res = await frontDeskApi.surchargeTypes();
        setSurchargeTypes(Array.isArray(res.data) ? res.data : []);
      } catch {
        setSurchargeTypes([]);
      }
    })();
  }, [kind]);

  useEffect(() => {
    const t = setTimeout(() => setQ(qDraft.trim()), 300);
    return () => clearTimeout(t);
  }, [qDraft]);

  useEffect(() => {
    setPage(1);
  }, [q, perPage, kind, propertyId]);

  const loadPricesForApartment = useCallback(
    async (aptId) => {
      if (!propertyId || !aptId) {
        setPriceRows([]);
        return;
      }
      try {
        const res = await frontDeskApi.apartmentPrices(propertyId, aptId);
        setPriceRows(Array.isArray(res.data) ? res.data : []);
      } catch {
        setPriceRows([]);
      }
    },
    [propertyId],
  );

  useEffect(() => {
    if (!modalOpen || kind === 'blocked-dates') return;
    const aid = kind === 'discounts' ? discountForm.apartment_id : surchargeForm.apartment_id;
    if (aid) loadPricesForApartment(aid);
    else setPriceRows([]);
  }, [modalOpen, kind, discountForm.apartment_id, surchargeForm.apartment_id, loadPricesForApartment]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const blob = [r.name, r.reason, r.apartment?.name].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(needle);
    });
  }, [rows, q]);

  const normPer = normalizeFdPerPage(perPage, 250);
  const pageCount = Math.max(1, Math.ceil(filtered.length / normPer));
  const safePage = Math.min(page, pageCount);
  const sliceFrom = (safePage - 1) * normPer;
  const pageRows = filtered.slice(sliceFrom, sliceFrom + normPer);
  const listMeta =
    filtered.length === 0
      ? null
      : {
          current_page: safePage,
          last_page: pageCount,
          per_page: normPer,
          total: filtered.length,
        };

  const openCreate = () => {
    setEditing(null);
    if (kind === 'discounts') {
      setDiscountForm({
        name: '',
        percent: '',
        min_nights: '',
        max_nights: '',
        valid_from: '',
        valid_to: '',
        is_active: true,
        sort_order: 0,
        apartment_id: '',
        apartment_price_id: '',
      });
    } else if (kind === 'surcharges') {
      setSurchargeForm({
        surcharge_type_id: surchargeTypes[0]?.id != null ? String(surchargeTypes[0].id) : '',
        name: '',
        billing_kind: 'fixed',
        amount: '',
        percent: '',
        min_nights: '',
        max_nights: '',
        valid_from: '',
        valid_to: '',
        is_active: true,
        is_mandatory: false,
        sort_order: 0,
        apartment_id: '',
        apartment_price_id: '',
      });
    } else {
      setBlockedForm({
        apartment_id: apartmentOptions[0]?.id != null ? String(apartmentOptions[0].id) : '',
        date_from: '',
        date_to: '',
        reason: '',
      });
    }
    setPriceRows([]);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    if (kind === 'discounts') {
      setDiscountForm({
        name: row.name || '',
        percent: row.percent != null ? String(row.percent) : '',
        min_nights: row.min_nights != null ? String(row.min_nights) : '',
        max_nights: row.max_nights != null ? String(row.max_nights) : '',
        valid_from: toDateInput(row.valid_from),
        valid_to: toDateInput(row.valid_to),
        is_active: row.is_active !== false,
        sort_order: row.sort_order ?? 0,
        apartment_id: row.apartment_id != null ? String(row.apartment_id) : '',
        apartment_price_id: row.apartment_price_id != null ? String(row.apartment_price_id) : '',
      });
    } else if (kind === 'surcharges') {
      const bk = row.billing_kind === 'percent' || row.billing_kind?.value === 'percent' ? 'percent' : 'fixed';
      setSurchargeForm({
        surcharge_type_id: row.surcharge_type_id != null ? String(row.surcharge_type_id) : '',
        name: row.name || '',
        billing_kind: bk,
        amount: row.amount != null ? formatWholeNairaFromNumber(row.amount) : '',
        percent: row.percent != null ? String(row.percent) : '',
        min_nights: row.min_nights != null ? String(row.min_nights) : '',
        max_nights: row.max_nights != null ? String(row.max_nights) : '',
        valid_from: toDateInput(row.valid_from),
        valid_to: toDateInput(row.valid_to),
        is_active: row.is_active !== false,
        is_mandatory: Boolean(row.is_mandatory),
        sort_order: row.sort_order ?? 0,
        apartment_id: row.apartment_id != null ? String(row.apartment_id) : '',
        apartment_price_id: row.apartment_price_id != null ? String(row.apartment_price_id) : '',
      });
    } else {
      setBlockedForm({
        apartment_id: row.apartment_id != null ? String(row.apartment_id) : '',
        date_from: toDateInput(row.date_from),
        date_to: toDateInput(row.date_to),
        reason: row.reason || '',
      });
    }
    setModalOpen(true);
  };

  const saveDiscount = async () => {
    const body = {
      name: discountForm.name.trim(),
      percent: Number(discountForm.percent),
      min_nights: discountForm.min_nights === '' ? null : Number(discountForm.min_nights),
      max_nights: discountForm.max_nights === '' ? null : Number(discountForm.max_nights),
      valid_from: discountForm.valid_from || null,
      valid_to: discountForm.valid_to || null,
      is_active: Boolean(discountForm.is_active),
      sort_order: Number(discountForm.sort_order) || 0,
      apartment_id: discountForm.apartment_id ? Number(discountForm.apartment_id) : null,
      apartment_price_id: discountForm.apartment_price_id ? Number(discountForm.apartment_price_id) : null,
    };
    if (editing) await frontDeskApi.updateDiscount(propertyId, editing.id, body);
    else await frontDeskApi.createDiscount(propertyId, body);
  };

  const saveSurcharge = async () => {
    const body = {
      surcharge_type_id: Number(surchargeForm.surcharge_type_id),
      name: surchargeForm.name.trim(),
      billing_kind: surchargeForm.billing_kind,
      amount:
        surchargeForm.billing_kind === 'fixed' && surchargeForm.amount !== ''
          ? parseWholeNairaToNumber(surchargeForm.amount)
          : null,
      percent: surchargeForm.billing_kind === 'percent' && surchargeForm.percent !== '' ? Number(surchargeForm.percent) : null,
      min_nights: surchargeForm.min_nights === '' ? null : Number(surchargeForm.min_nights),
      max_nights: surchargeForm.max_nights === '' ? null : Number(surchargeForm.max_nights),
      valid_from: surchargeForm.valid_from || null,
      valid_to: surchargeForm.valid_to || null,
      is_active: Boolean(surchargeForm.is_active),
      is_mandatory: Boolean(surchargeForm.is_mandatory),
      sort_order: Number(surchargeForm.sort_order) || 0,
      apartment_id: surchargeForm.apartment_id ? Number(surchargeForm.apartment_id) : null,
      apartment_price_id: surchargeForm.apartment_price_id ? Number(surchargeForm.apartment_price_id) : null,
    };
    if (editing) await frontDeskApi.updateSurcharge(propertyId, editing.id, body);
    else await frontDeskApi.createSurcharge(propertyId, body);
  };

  const saveBlocked = async () => {
    const body = {
      apartment_id: Number(blockedForm.apartment_id),
      date_from: blockedForm.date_from,
      date_to: blockedForm.date_to,
      reason: blockedForm.reason.trim() || null,
    };
    if (editing) await frontDeskApi.updateBlockedDate(propertyId, editing.id, body);
    else await frontDeskApi.createBlockedDate(propertyId, body);
  };

  const handleSave = async () => {
    setBusy(true);
    setError('');
    try {
      if (kind === 'discounts') await saveDiscount();
      else if (kind === 'surcharges') await saveSurcharge();
      else await saveBlocked();
      setSuccessMessage(editing ? 'Saved.' : 'Created.');
      setModalOpen(false);
      await loadList();
    } catch (e) {
      setError(e.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (row) => {
    const ok = await luxisttConfirm({
      title: 'Delete this row?',
      text: 'This cannot be undone.',
      icon: 'warning',
      confirmText: 'Delete',
    });
    if (!ok) return;
    setError('');
    try {
      if (kind === 'discounts') await frontDeskApi.deleteDiscount(propertyId, row.id);
      else if (kind === 'surcharges') await frontDeskApi.deleteSurcharge(propertyId, row.id);
      else await frontDeskApi.deleteBlockedDate(propertyId, row.id);
      setSuccessMessage('Deleted.');
      await loadList();
    } catch (e) {
      setError(e.message || 'Could not delete.');
    }
  };

  if (!canView) {
    return (
      <>
        <h2 className="front-desk-section-title">{meta.title}</h2>
        <p className="front-desk-section-lead">{meta.lead}</p>
        <p className="muted">You do not have catalog permissions for this section.</p>
      </>
    );
  }

  return (
    <>
      <h2 className="front-desk-section-title">{meta.title}</h2>
      <p className="front-desk-section-lead">{meta.lead}</p>

      {!propertyId ? (
        <p className="muted">Select a property above.</p>
      ) : kind === 'blocked-dates' && apartmentOptions.length === 0 ? (
        <p className="muted">This property has no apartments yet. Add units in admin before blocking dates.</p>
      ) : (
        <>
          <div className="fd-bookings-toolbar" style={{ marginBottom: '1rem' }}>
            <label className="fd-bookings-toolbar-field">
              <span className="fd-bookings-toolbar-label">Search</span>
              <input type="search" value={qDraft} onChange={(e) => setQDraft(e.target.value)} placeholder="Filter…" />
            </label>
            <FdPerPageField id={`fd-catalog-${kind}-per-page`} value={perPage} onChange={setPerPage} maxAllowed={250} />
            {canManage ? (
              <div className="fd-bookings-toolbar-actions">
                <button type="button" className="btn-primary fd-btn-with-icon" onClick={openCreate}>
                  <FiPlus aria-hidden />
                  New
                </button>
              </div>
            ) : null}
          </div>

          {loading ? (
            <p className="muted">Loading…</p>
          ) : (
            <div className="fd-table-wrap fd-table-wrap--scroll">
              <table className="fd-table fd-table--wide fd-table--nowrap-cells">
                <thead>
                  <tr>
                    {kind === 'discounts' ? (
                      <>
                        <th>Name</th>
                        <th className="fd-col-right">%</th>
                        <th>Scope</th>
                        <th className="fd-col-center">Active</th>
                        {canManage ? <th className="fd-actions-col-start">Actions</th> : null}
                      </>
                    ) : null}
                    {kind === 'surcharges' ? (
                      <>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Fee</th>
                        <th>Scope</th>
                        <th className="fd-col-center">Active</th>
                        {canManage ? <th className="fd-actions-col-start">Actions</th> : null}
                      </>
                    ) : null}
                    {kind === 'blocked-dates' ? (
                      <>
                        <th>Unit</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Reason</th>
                        {canManage ? <th className="fd-actions-col-start">Actions</th> : null}
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <EmptyTableRow
                      colSpan={kind === 'surcharges' ? (canManage ? 6 : 5) : kind === 'discounts' ? (canManage ? 5 : 4) : canManage ? 5 : 4}
                      message="No rows yet."
                    />
                  ) : (
                    pageRows.map((row) => {
                      if (kind === 'discounts') {
                        return (
                          <tr key={row.id}>
                            <td style={{ fontWeight: 600 }}>{row.name}</td>
                            <td className="fd-col-right">{row.percent != null ? `${row.percent}%` : '—'}</td>
                            <td className="muted fd-td-ellipsis" style={{ fontSize: '0.875rem' }}>
                              {row.apartment?.name ? row.apartment.name : 'Property-wide'}
                            </td>
                            <td className="fd-col-center">{row.is_active === false ? 'No' : 'Yes'}</td>
                            {canManage ? (
                              <td className="fd-actions-col-start">
                                <div className="fd-finance-row-actions">
                                  <button type="button" className="fd-table-action-pill" onClick={() => openEdit(row)}>
                                    Edit
                                  </button>
                                  <button type="button" className="fd-table-action-pill fd-table-action-pill--danger" onClick={() => handleDelete(row)}>
                                    Delete
                                  </button>
                                </div>
                              </td>
                            ) : null}
                          </tr>
                        );
                      }
                      if (kind === 'surcharges') {
                        const bk = row.billing_kind === 'percent' || row.billing_kind?.value === 'percent' ? 'percent' : 'fixed';
                        const fee =
                          bk === 'fixed' && row.amount != null
                            ? formatNgn(row.amount)
                            : bk === 'percent' && row.percent != null
                              ? `${row.percent}%`
                              : '—';
                        return (
                          <tr key={row.id}>
                            <td style={{ fontWeight: 600 }}>{row.name}</td>
                            <td>{row.surcharge_type?.name || '—'}</td>
                            <td>{fee}</td>
                            <td className="muted" style={{ fontSize: '0.875rem' }}>
                              {row.apartment?.name ? row.apartment.name : 'Property-wide'}
                            </td>
                            <td className="fd-col-center">{row.is_active === false ? 'No' : 'Yes'}</td>
                            {canManage ? (
                              <td className="fd-actions-col-start">
                                <div className="fd-finance-row-actions">
                                  <button type="button" className="fd-table-action-pill" onClick={() => openEdit(row)}>
                                    Edit
                                  </button>
                                  <button type="button" className="fd-table-action-pill fd-table-action-pill--danger" onClick={() => handleDelete(row)}>
                                    Delete
                                  </button>
                                </div>
                              </td>
                            ) : null}
                          </tr>
                        );
                      }
                      return (
                        <tr key={row.id}>
                          <td>{row.apartment?.name || '—'}</td>
                          <td>{toDateInput(row.date_from)}</td>
                          <td>{toDateInput(row.date_to)}</td>
                          <td className="muted" style={{ fontSize: '0.875rem' }}>
                            {row.reason || '—'}
                          </td>
                          {canManage ? (
                            <td className="fd-actions-col-start">
                              <div className="fd-finance-row-actions">
                                <button type="button" className="fd-table-action-pill" onClick={() => openEdit(row)}>
                                  Edit
                                </button>
                                <button type="button" className="fd-table-action-pill fd-table-action-pill--danger" onClick={() => handleDelete(row)}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {listMeta ? <FdPaginationNav meta={listMeta} page={safePage} setPage={setPage} disabled={loading} /> : null}
        </>
      )}

      {modalOpen && propertyId ? (
        <div
          className="modal-backdrop fd-modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setModalOpen(false);
          }}
        >
          <div
            className="modal-box modal-wide"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560 }}
          >
            <h3>{editing ? `Edit ${meta.title.slice(0, -1)}` : `New ${meta.title.slice(0, -1)}`}</h3>
            {kind === 'discounts' ? (
              <div className="fd-finance-form__grid" style={{ marginTop: 12 }}>
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Name</span>
                  <input
                    className="fd-finance-input"
                    value={discountForm.name}
                    onChange={(e) => setDiscountForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">Percent</span>
                  <input
                    type="number"
                    className="fd-finance-input"
                    min={0.01}
                    max={100}
                    step={0.01}
                    value={discountForm.percent}
                    onChange={(e) => setDiscountForm((f) => ({ ...f, percent: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">Sort</span>
                  <input
                    type="number"
                    className="fd-finance-input"
                    min={0}
                    value={discountForm.sort_order}
                    onChange={(e) => setDiscountForm((f) => ({ ...f, sort_order: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">Min nights</span>
                  <input
                    type="number"
                    className="fd-finance-input"
                    min={1}
                    value={discountForm.min_nights}
                    onChange={(e) => setDiscountForm((f) => ({ ...f, min_nights: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">Max nights</span>
                  <input
                    type="number"
                    className="fd-finance-input"
                    min={1}
                    value={discountForm.max_nights}
                    onChange={(e) => setDiscountForm((f) => ({ ...f, max_nights: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">Valid from</span>
                  <input
                    type="date"
                    className="fd-finance-input"
                    value={discountForm.valid_from}
                    onChange={(e) => setDiscountForm((f) => ({ ...f, valid_from: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">Valid to</span>
                  <input
                    type="date"
                    className="fd-finance-input"
                    value={discountForm.valid_to}
                    onChange={(e) => setDiscountForm((f) => ({ ...f, valid_to: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Apartment (empty = property-wide)</span>
                  <select
                    className="fd-finance-input"
                    value={discountForm.apartment_id}
                    onChange={(e) =>
                      setDiscountForm((f) => ({
                        ...f,
                        apartment_id: e.target.value,
                        apartment_price_id: '',
                      }))
                    }
                    disabled={busy}
                  >
                    <option value="">Property-wide</option>
                    {apartmentOptions.map((a) => (
                      <option key={a.id} value={String(a.id)}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Price period (optional)</span>
                  <select
                    className="fd-finance-input"
                    value={discountForm.apartment_price_id}
                    onChange={(e) => setDiscountForm((f) => ({ ...f, apartment_price_id: e.target.value }))}
                    disabled={busy || !discountForm.apartment_id}
                  >
                    <option value="">Any period</option>
                    {priceRows.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.start_date} → {p.end_date} · {formatNgn(p.price)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fd-finance-field fd-bookings-toolbar-field--checkbox fd-finance-field--span2">
                  <input
                    type="checkbox"
                    checked={discountForm.is_active}
                    onChange={(e) => setDiscountForm((f) => ({ ...f, is_active: e.target.checked }))}
                    disabled={busy}
                  />
                  <span>Active</span>
                </label>
              </div>
            ) : null}

            {kind === 'surcharges' ? (
              <div className="fd-finance-form__grid" style={{ marginTop: 12 }}>
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Surcharge type</span>
                  <select
                    className="fd-finance-input"
                    value={surchargeForm.surcharge_type_id}
                    onChange={(e) => setSurchargeForm((f) => ({ ...f, surcharge_type_id: e.target.value }))}
                    disabled={busy}
                  >
                    {surchargeTypes.map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Name</span>
                  <input
                    className="fd-finance-input"
                    value={surchargeForm.name}
                    onChange={(e) => setSurchargeForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">Billing</span>
                  <select
                    className="fd-finance-input"
                    value={surchargeForm.billing_kind}
                    onChange={(e) => setSurchargeForm((f) => ({ ...f, billing_kind: e.target.value }))}
                    disabled={busy}
                  >
                    <option value="fixed">Fixed (₦)</option>
                    <option value="percent">Percent of room</option>
                  </select>
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">Sort</span>
                  <input
                    type="number"
                    className="fd-finance-input"
                    min={0}
                    value={surchargeForm.sort_order}
                    onChange={(e) => setSurchargeForm((f) => ({ ...f, sort_order: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                {surchargeForm.billing_kind === 'fixed' ? (
                  <label className="fd-finance-field">
                    <span className="fd-finance-field__label">Amount (₦)</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="fd-finance-input luxistt-money-input"
                      value={surchargeForm.amount}
                      onChange={(e) =>
                        setSurchargeForm((f) => ({ ...f, amount: formatWholeNairaInputChange(e.target.value) }))
                      }
                      disabled={busy}
                    />
                  </label>
                ) : (
                  <label className="fd-finance-field">
                    <span className="fd-finance-field__label">Percent</span>
                    <input
                      type="number"
                      className="fd-finance-input"
                      min={0.01}
                      max={100}
                      step={0.01}
                      value={surchargeForm.percent}
                      onChange={(e) => setSurchargeForm((f) => ({ ...f, percent: e.target.value }))}
                      disabled={busy}
                    />
                  </label>
                )}
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Apartment (empty = property-wide)</span>
                  <select
                    className="fd-finance-input"
                    value={surchargeForm.apartment_id}
                    onChange={(e) =>
                      setSurchargeForm((f) => ({
                        ...f,
                        apartment_id: e.target.value,
                        apartment_price_id: '',
                      }))
                    }
                    disabled={busy}
                  >
                    <option value="">Property-wide</option>
                    {apartmentOptions.map((a) => (
                      <option key={a.id} value={String(a.id)}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Price period (optional)</span>
                  <select
                    className="fd-finance-input"
                    value={surchargeForm.apartment_price_id}
                    onChange={(e) => setSurchargeForm((f) => ({ ...f, apartment_price_id: e.target.value }))}
                    disabled={busy || !surchargeForm.apartment_id}
                  >
                    <option value="">Any period</option>
                    {priceRows.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.start_date} → {p.end_date} · {formatNgn(p.price)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fd-finance-field fd-bookings-toolbar-field--checkbox">
                  <input
                    type="checkbox"
                    checked={surchargeForm.is_active}
                    onChange={(e) => setSurchargeForm((f) => ({ ...f, is_active: e.target.checked }))}
                    disabled={busy}
                  />
                  <span>Active</span>
                </label>
                <label className="fd-finance-field fd-bookings-toolbar-field--checkbox">
                  <input
                    type="checkbox"
                    checked={surchargeForm.is_mandatory}
                    onChange={(e) => setSurchargeForm((f) => ({ ...f, is_mandatory: e.target.checked }))}
                    disabled={busy}
                  />
                  <span>Mandatory</span>
                </label>
              </div>
            ) : null}

            {kind === 'blocked-dates' ? (
              <div className="fd-finance-form__grid" style={{ marginTop: 12 }}>
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Apartment</span>
                  <select
                    className="fd-finance-input"
                    value={blockedForm.apartment_id}
                    onChange={(e) => setBlockedForm((f) => ({ ...f, apartment_id: e.target.value }))}
                    disabled={busy}
                  >
                    {apartmentOptions.map((a) => (
                      <option key={a.id} value={String(a.id)}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">From</span>
                  <input
                    type="date"
                    className="fd-finance-input"
                    value={blockedForm.date_from}
                    onChange={(e) => setBlockedForm((f) => ({ ...f, date_from: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">To</span>
                  <input
                    type="date"
                    className="fd-finance-input"
                    value={blockedForm.date_to}
                    onChange={(e) => setBlockedForm((f) => ({ ...f, date_to: e.target.value }))}
                    disabled={busy}
                  />
                </label>
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Reason (optional)</span>
                  <input
                    className="fd-finance-input"
                    value={blockedForm.reason}
                    onChange={(e) => setBlockedForm((f) => ({ ...f, reason: e.target.value }))}
                    disabled={busy}
                  />
                </label>
              </div>
            ) : null}

            <div className="fd-booking-modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => !busy && setModalOpen(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
